// components/AndroidInstallModal.jsx
// Branded install prompt for Android. Two modes:
//  - Native: when the browser fired `beforeinstallprompt` (Chromium on the
//    deployed HTTPS site), the primary button hands off to the one-tap
//    native install dialog.
//  - Manual: any other Android browser (Firefox, Samsung Internet, or
//    Chromium before the event fires — e.g. localhost, where the manifest's
//    absolute start_url blocks installability) gets step-by-step
//    instructions tailored to that browser.
// Shares styling with IOSInstallModal via install-modal.css.
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import { storageService } from '../utils/storageService';

const AndroidInstallModal = ({ deferredPrompt, onPromptResult }) => {
  const [showModal, setShowModal] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [browser, setBrowser] = useState('chrome');
  const { language } = useLanguage();

  // Samsung Internet must be checked before Chrome — its UA contains both
  // "SamsungBrowser" and "Chrome".
  const detectBrowser = () => {
    const ua = window.navigator.userAgent;
    if (/SamsungBrowser/i.test(ua)) return 'samsung';
    if (/Firefox/i.test(ua)) return 'firefox';
    if (/EdgA/i.test(ua)) return 'edge';
    if (/OPR|Opera/i.test(ua)) return 'opera';
    return 'chrome';
  };

  const isRunningStandalone = () =>
    window.matchMedia('(display-mode: standalone)').matches;

  const hasUserDismissed = () =>
    storageService.getItem('androidInstallModalDismissed', false);

  // Set once the app is installed. The tab that ran the install stays open in
  // the browser (non-standalone), so without this flag the modal would come
  // straight back in its manual "Add to Home screen" mode once `deferredPrompt`
  // is cleared.
  const hasInstalled = () => storageService.getItem('pwaInstalled', false);

  // "Maybe later" hides it for the rest of this session only
  const closedThisSession = useRef(false);

  useEffect(() => {
    const androidDetected = /android/i.test(window.navigator.userAgent);
    setIsAndroid(androidDetected);
    setBrowser(detectBrowser());

    // Trigger on Android UA (manual instructions) or on beforeinstallprompt
    // arriving (native install — also covers desktop Chromium).
    const eligible = androidDetected || Boolean(deferredPrompt);
    if (
      eligible &&
      !isRunningStandalone() &&
      !hasUserDismissed() &&
      !hasInstalled() &&
      !closedThisSession.current
    ) {
      const timer = setTimeout(() => setShowModal(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [deferredPrompt]);

  // Covers installs that don't go through our button (browser menu, or the
  // native mini-infobar) — close the modal and never offer it again.
  useEffect(() => {
    const handleAppInstalled = () => {
      storageService.setItem('pwaInstalled', true);
      closedThisSession.current = true;
      setShowModal(false);
    };
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt || isInstalling) return;
    setIsInstalling(true);
    try {
      deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      if (choiceResult.outcome === 'accepted') {
        storageService.setItem('pwaInstalled', true);
      }
      // Either way, don't re-open this session: clearing `deferredPrompt`
      // re-runs the effect above, which would otherwise show the manual steps.
      closedThisSession.current = true;
      if (onPromptResult) {
        onPromptResult(choiceResult.outcome);
      }
    } finally {
      setIsInstalling(false);
      setShowModal(false);
    }
  };

  const handleDontShowAgain = () => {
    storageService.setItem('androidInstallModalDismissed', true);
    setShowModal(false);
  };

  const handleClose = () => {
    closedThisSession.current = true;
    setShowModal(false);
  };

  const texts = {
    en: {
      title: 'Install the HELEXPO App',
      subtitle: 'One tap — no app store needed',
      hero: 'Installs instantly from your browser. It takes seconds and uses almost no storage.',
      heroManual: 'Add it to your Home Screen straight from the browser — it takes seconds and uses almost no storage.',
      benefitsTitle: 'Why install it',
      benefit1: 'Faster loading',
      benefit2: 'Works offline',
      benefit3: 'Full-screen view',
      benefit4: 'One tap from Home',
      install: 'Install app',
      installing: 'Opening installer…',
      dontShowAgain: "Don't show again",
      close: 'Maybe later',
      steps: {
        chrome: [
          { icon: 'bi-three-dots-vertical', title: 'Open the browser menu', detail: 'Tap the three-dot menu in the top-right corner of Chrome.' },
          { icon: 'bi-plus-square', title: "Tap 'Add to Home screen'", detail: "Choose 'Install app' or 'Add to Home screen' from the menu." },
          { icon: 'bi-check2-circle', title: "Tap 'Install' to finish", detail: 'The HELEXPO icon appears on your Home Screen, ready to open like any app.' },
        ],
        firefox: [
          { icon: 'bi-three-dots-vertical', title: 'Open the browser menu', detail: 'Tap the three-dot menu in Firefox.' },
          { icon: 'bi-plus-square', title: "Tap 'Add to Home screen'", detail: "Find 'Add to Home screen' (or 'Install') in the menu." },
          { icon: 'bi-check2-circle', title: "Tap 'Add' to finish", detail: 'The HELEXPO icon appears on your Home Screen, ready to open like any app.' },
        ],
        samsung: [
          { icon: 'bi-list', title: 'Open the browser menu', detail: 'Tap the menu button (three lines) in the bottom-right corner of Samsung Internet.' },
          { icon: 'bi-plus-square', title: "Tap 'Add page to'", detail: "Choose 'Add page to' and then select 'Home screen'." },
          { icon: 'bi-check2-circle', title: "Tap 'Add' to finish", detail: 'The HELEXPO icon appears on your Home Screen, ready to open like any app.' },
        ],
      },
    },
    gr: {
      title: 'Εγκατάσταση HELEXPO App',
      subtitle: 'Με ένα πάτημα — χωρίς app store',
      hero: 'Εγκαθίσταται άμεσα από τον browser σας. Διαρκεί λίγα δευτερόλεπτα και δεν πιάνει σχεδόν καθόλου χώρο.',
      heroManual: 'Προσθέστε την στην Αρχική Οθόνη απευθείας από τον browser — διαρκεί λίγα δευτερόλεπτα και δεν πιάνει σχεδόν καθόλου χώρο.',
      benefitsTitle: 'Γιατί να την εγκαταστήσετε',
      benefit1: 'Ταχύτερη φόρτωση',
      benefit2: 'Λειτουργεί χωρίς σύνδεση',
      benefit3: 'Πλήρης οθόνη',
      benefit4: 'Άμεση πρόσβαση',
      install: 'Εγκατάσταση',
      installing: 'Άνοιγμα εγκατάστασης…',
      dontShowAgain: 'Να μην εμφανιστεί ξανά',
      close: 'Ίσως αργότερα',
      steps: {
        chrome: [
          { icon: 'bi-three-dots-vertical', title: 'Ανοίξτε το μενού του browser', detail: 'Πατήστε το μενού με τις τρεις τελείες πάνω δεξιά στο Chrome.' },
          { icon: 'bi-plus-square', title: "Πατήστε 'Προσθήκη στην Αρχική οθόνη'", detail: "Επιλέξτε 'Εγκατάσταση εφαρμογής' ή 'Προσθήκη στην Αρχική οθόνη' από το μενού." },
          { icon: 'bi-check2-circle', title: "Πατήστε 'Εγκατάσταση' για ολοκλήρωση", detail: 'Το εικονίδιο HELEXPO εμφανίζεται στην Αρχική Οθόνη, έτοιμο να ανοίξει σαν κανονική εφαρμογή.' },
        ],
        firefox: [
          { icon: 'bi-three-dots-vertical', title: 'Ανοίξτε το μενού του browser', detail: 'Πατήστε το μενού με τις τρεις τελείες στο Firefox.' },
          { icon: 'bi-plus-square', title: "Πατήστε 'Προσθήκη στην Αρχική οθόνη'", detail: "Βρείτε την επιλογή 'Προσθήκη στην Αρχική οθόνη' (ή 'Εγκατάσταση') στο μενού." },
          { icon: 'bi-check2-circle', title: "Πατήστε 'Προσθήκη' για ολοκλήρωση", detail: 'Το εικονίδιο HELEXPO εμφανίζεται στην Αρχική Οθόνη, έτοιμο να ανοίξει σαν κανονική εφαρμογή.' },
        ],
        samsung: [
          { icon: 'bi-list', title: 'Ανοίξτε το μενού του browser', detail: 'Πατήστε το κουμπί μενού (τρεις γραμμές) κάτω δεξιά στο Samsung Internet.' },
          { icon: 'bi-plus-square', title: "Πατήστε 'Προσθήκη σελίδας σε'", detail: "Επιλέξτε 'Προσθήκη σελίδας σε' και μετά 'Αρχική οθόνη'." },
          { icon: 'bi-check2-circle', title: "Πατήστε 'Προσθήκη' για ολοκλήρωση", detail: 'Το εικονίδιο HELEXPO εμφανίζεται στην Αρχική Οθόνη, έτοιμο να ανοίξει σαν κανονική εφαρμογή.' },
        ],
      },
    },
  };

  const t = texts[language] || texts.en;
  const canNativeInstall = Boolean(deferredPrompt);

  if (!isAndroid && !canNativeInstall) {
    return null;
  }

  // Edge/Opera menus follow the Chrome layout closely enough
  const manualSteps = t.steps[browser] || t.steps.chrome;

  const benefits = [
    { icon: 'bi-lightning-charge-fill', label: t.benefit1 },
    { icon: 'bi-wifi-off', label: t.benefit2 },
    { icon: 'bi-arrows-fullscreen', label: t.benefit3 },
    { icon: 'bi-house-door-fill', label: t.benefit4 },
  ];

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      className="pwa-install-modal"
      backdrop="static"
    >
      <Modal.Header className="pwa-install-header border-0">
        <div className="pwa-install-header-icon" aria-hidden="true">
          <i className="bi bi-android2" />
        </div>
        <div className="pwa-install-header-text">
          <Modal.Title className="pwa-install-title">{t.title}</Modal.Title>
          <div className="pwa-install-subtitle">{t.subtitle}</div>
        </div>
      </Modal.Header>

      <Modal.Body className="pwa-install-body">
        <div className="pwa-install-hero">
          <i className="bi bi-download" aria-hidden="true" />
          <div className="pwa-install-hero-text">
            {canNativeInstall ? t.hero : t.heroManual}
          </div>
        </div>

        {!canNativeInstall && (
          <div className="pwa-install-steps">
            {manualSteps.map((step, index) => (
              <div className="pwa-install-step" key={step.icon}>
                <div className="pwa-install-step-rail" aria-hidden="true">
                  <span className="pwa-install-step-badge">
                    <i className={`bi ${step.icon}`} />
                  </span>
                  {index < manualSteps.length - 1 && <span className="pwa-install-step-line" />}
                </div>
                <div className="pwa-install-step-content">
                  <div className="pwa-install-step-title">{step.title}</div>
                  <div className="pwa-install-step-detail">{step.detail}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pwa-install-benefits">
          <div className="pwa-install-benefits-title">{t.benefitsTitle}</div>
          <div className="pwa-install-benefits-grid">
            {benefits.map((benefit) => (
              <div className="pwa-install-benefit" key={benefit.icon}>
                <i className={`bi ${benefit.icon}`} aria-hidden="true" />
                <span>{benefit.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pwa-install-actions--stacked">
          {canNativeInstall && (
            <Button
              className="pwa-install-btn-primary"
              onClick={handleInstall}
              disabled={isInstalling}
            >
              <i className="bi bi-download me-2" aria-hidden="true" />
              {isInstalling ? t.installing : t.install}
            </Button>
          )}
          <div className="pwa-install-actions-row">
            <Button className="pwa-install-btn-ghost" onClick={handleDontShowAgain}>
              {t.dontShowAgain}
            </Button>
            <Button
              className={canNativeInstall ? 'pwa-install-btn-ghost' : 'pwa-install-btn-primary'}
              onClick={handleClose}
            >
              {t.close}
            </Button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AndroidInstallModal;
